/**
 * Bas du tableau de bord élève (migration 0320) : le PACK du moment et le
 * PROGRAMME complet de son niveau, avec SON prix (déduction plafonnée de ce
 * qu'il a déjà acheté, toujours mentionnée) et l'achat direct (Stripe).
 * Rien s'il possède déjà tout, s'il a un abonnement / pass (tout inclus) ou
 * dans l'app native (achats des cartes sur le site — App Store 3.1.1).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { cardPalette, type SkillcardQuote, offerContentLabel } from '@jeraisonne/db/cards';
import { levelLabel, packPeriodLabel, subjectLabel } from '@jeraisonne/db/curriculum';
import { supabase } from '@/lib/supabase';
import { canBuySkillcardsInApp, storeQuote } from '@/lib/skillcards/iap';
import { skillcardCheckoutErrorMessage, startSkillcardCheckout } from '@/lib/skillcards/purchase';
import { SkillcardOfferRow } from './skillcard-offer-row';
import { SkillcardPromoBanner } from '@/features/skillcards/skillcard-promo-banner';

const db = supabase as unknown as SupabaseClient;

export interface ProgrammeOffer {
  subject: string;
  programme_id: string;
  programme: SkillcardQuote | null;
  programme_card_id: string | null;
  pack:
    | (SkillcardQuote & { pack_title: string; start_month: number; end_month: number | null })
    | null;
  pack_card_id: string | null;
}

/** Offres à montrer : seulement ce qu'il reste à acheter. */
export function visibleOffers(offers: readonly ProgrammeOffer[]): ProgrammeOffer[] {
  return offers
    .map((o) => ({
      ...o,
      pack: o.pack && o.pack.cards_to_buy > 0 && o.pack_card_id ? o.pack : null,
      programme:
        o.programme && o.programme.cards_to_buy > 0 && o.programme_card_id ? o.programme : null,
    }))
    .filter((o) => o.pack || o.programme);
}

export function useMyProgrammeOffers(userId: string | null | undefined) {
  return useQuery({
    queryKey: ['my-programme-offers', userId],
    enabled: !!userId && canBuySkillcardsInApp(),
    staleTime: 60_000,
    queryFn: async (): Promise<ProgrammeOffer[]> => {
      const { data, error } = await db.rpc('get_my_programme_offers');
      if (error) throw new Error(error.message);
      const list = ((data as { offers?: ProgrammeOffer[] } | null)?.offers ??
        []) as ProgrammeOffer[];
      // App native : prix du palier du store (migration 0351).
      return list.map((o) => ({
        ...o,
        pack: o.pack && storeQuote(o.pack),
        programme: o.programme && storeQuote(o.programme),
      }));
    },
  });
}

export function ProgrammeOffersSection({
  userId,
  level,
  only,
  returnPath = '/student/dashboard',
  title,
}: {
  userId: string;
  level: string | null | undefined;
  /** `programme` : n'affiche que le programme complet (ex. débloquer les automatismes). */
  only?: 'programme';
  /** Chemin interne où revenir après paiement. */
  returnPath?: string;
  title?: string;
}) {
  const offers = useMyProgrammeOffers(userId);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list = visibleOffers(
    (offers.data ?? []).map((o) => (only === 'programme' ? { ...o, pack: null } : o)),
  );
  if (!canBuySkillcardsInApp() || list.length === 0) return null;
  const palette = cardPalette(level ? [level] : []);

  async function buy(kind: 'pack' | 'programme', targetId: string, cardId: string) {
    setBusy(targetId);
    setError(null);
    try {
      const url = await startSkillcardCheckout({
        kind,
        targetId,
        cardId,
        returnPath,
      });
      window.location.href = url;
    } catch (e) {
      setError(skillcardCheckoutErrorMessage(e instanceof Error ? e.message : ''));
      setBusy(null);
    }
  }

  return (
    <section aria-labelledby="programme-offers-title" className="space-y-3">
      <h2 id="programme-offers-title" className="font-display text-ink-900 text-lg font-bold">
        {title ?? `Débloque ton année${level ? ` de ${levelLabel(level)}` : ''}`}
      </h2>
      <SkillcardPromoBanner />
      {list.map((o) => (
        <div key={o.programme_id} className="space-y-2">
          {list.length > 1 && (
            <p className="text-ink-500 text-xs font-semibold uppercase tracking-wider">
              {subjectLabel(o.subject)}
            </p>
          )}
          {o.pack && (
            <SkillcardOfferRow
              quote={o.pack}
              title={o.pack.pack_title}
              subtitle={`Pack du moment · ${packPeriodLabel(o.pack.start_month, o.pack.end_month)} · ${offerContentLabel(o.pack)}`}
              accent={palette.accent}
              background={palette.bg}
              busy={busy === o.pack.target_id}
              disabled={!!busy}
              onBuy={() => void buy('pack', o.pack!.target_id, o.pack_card_id!)}
            />
          )}
          {o.programme && (
            <SkillcardOfferRow
              quote={o.programme}
              title={`Programme complet · ${subjectLabel(o.subject)}`}
              subtitle={`${offerContentLabel(o.programme)} · toute l’année`}
              highlight="Le plus avantageux"
              accent={palette.accent}
              background={palette.bg}
              busy={busy === o.programme.target_id}
              disabled={!!busy}
              onBuy={() => void buy('programme', o.programme!.target_id, o.programme_card_id!)}
            />
          )}
        </div>
      ))}
      {error && (
        <p role="alert" className="text-error-600 text-sm">
          {error}
        </p>
      )}
    </section>
  );
}
